import {
  CreateReducer,
  CreateAction,
} from '../utils/store'

import {
  handlers,
} from '../utils/api'

import {
  ProjectState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  NetworkThunk,
} from '../utils/network'

import {
  IRecord_Project,
} from '@kaidam/fc-shared/src/types/schema/project'

import {
  IData_Project_Settings,
} from '@kaidam/fc-shared/src/types/project'

import {
  IData_UserSummary,
} from '@kaidam/fc-shared/src/types/schema/user_summary'

import projectSelectors from '../selectors/project'

const prefix = 'project'

/*

  initial state

*/
const initialState: ProjectState = {
  currentProjectId: null,
  currentOrgId: null,
  data: [],
  summary: null,
}

/*

  reducers

*/
const setCurrentProjectId: ReducerHandler<ProjectState, string> = (state, action) => {
  localStorage.setItem('currentProjectId', action.payload)
  state.currentProjectId = action.payload
}

const setCurrentOrgId: ReducerHandler<ProjectState, string | null> = (state, action) => {
  localStorage.setItem('currentOrgId', action.payload || '')
  state.currentOrgId = action.payload
}

const setData: ReducerHandler<ProjectState, IRecord_Project[]> = (state, action) => {
  state.data = action.payload
}

const setSummary: ReducerHandler<ProjectState, IData_UserSummary> = (state, action) => {
  state.summary = action.payload
}

/*

  thunks

*/

// force the project to switch for the case where we are deep linking to a specific project
const initialise: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const urlParams = new URLSearchParams(window.location.search)
  const focusProjectId = urlParams.get('focusProjectId')
  if(focusProjectId) {
    dispatch(actions.setCurrentProjectId(focusProjectId))
    await dispatch(loadUserSummary(focusProjectId))
  }
}


const list: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const result = await handlers.get<IRecord_Project[]>('/user/projects')
  dispatch(actions.setData(result))
  let currentProjectId = localStorage.getItem('currentProjectId')
  // we have cached a project not in the list
  if(!result.find(p => p.id == currentProjectId)) {
    currentProjectId = ''
    localStorage.setItem('currentProjectId', '')
  }
  if(!currentProjectId && result.length > 0) {
    currentProjectId = result[0].id
  }
  if(currentProjectId) {
    await dispatch(setCurrentProject(currentProjectId))
  }
}

const loadUserSummary: ThunkHandler<string | void, void> = (projectId) => async (dispatch, getState) => {
  if(!projectId) {
    const systemProjectId = projectSelectors.currentProjectId(getState())
    if(systemProjectId) {
      projectId = systemProjectId
    }
  }
  let currentOrgId = localStorage.getItem('currentOrgId')
  const summary = await handlers.get<IData_UserSummary>(`/user/summary/${projectId}`, {
    params: {
      org: currentOrgId || '',
    }
  })
  dispatch(actions.setSummary(summary))
  if(currentOrgId && summary.orgs.find(orgId => orgId == currentOrgId)) {
    dispatch(actions.setCurrentOrgId(currentOrgId))
  }
  else {
    dispatch(actions.setCurrentOrgId(summary.orgs[0] || null))
  }
}



const updateProjectSettings: ThunkHandler<IData_Project_Settings, void> = (data) => async (dispatch, getState) => {
  const projectHandlers = getProjectHandlers(getState)
  await projectHandlers.put(`/project_settings`, data)
  await dispatch(actions.list())
}

const setCurrentProject: ThunkHandler<string, void> = (projectId) => async (dispatch, getState) => {
  dispatch(actions.setCurrentProjectId(projectId))
  await dispatch(loadUserSummary(projectId))
}

const setCurrentOrg: ThunkHandler<{
  project: string,
  org: string,
}, void> = ({project, org}) => async (dispatch, getState) => {
  dispatch(actions.setCurrentOrgId(org))
  await dispatch(loadUserSummary(project))
}

/*

  collections

*/
const reducers = {
  setCurrentProjectId,
  setCurrentOrgId,
  setData,
  setSummary,
}

export const actions = {
  setCurrentProjectId: CreateAction(prefix, `setCurrentProjectId`, reducers.setCurrentProjectId),
  setCurrentOrgId: CreateAction(prefix, `setCurrentOrgId`, reducers.setCurrentOrgId),
  setData: CreateAction(prefix, `setData`, reducers.setData),
  setSummary: CreateAction(prefix, `setSummary`, reducers.setSummary),
  initialise,
  list,
  loadUserSummary,
  setCurrentProject,
  setCurrentOrg,
  updateProjectSettings: NetworkThunk(prefix, `updateProjectSettings`, updateProjectSettings, {
    snackbar: true,
    globalLoading: {
      message: 'updating settings...'
    },
  }),
}

export const reducer = CreateReducer<ProjectState>({
  prefix,
  initialState,
  reducers,
})

export default actions
